import React from 'react'
import styled from 'styled-components'
import { Container, Kicker, MOBILE } from '../CustomLanding.styled'
import { colors, modernExam } from '../content'

const ModernExam: React.FC = () => (
	<Section>
		<Container>
			<Split>
				<HeadCol>
					<Kicker color={colors.maroon}>{modernExam.kicker}</Kicker>
					<Title>{modernExam.title}</Title>
				</HeadCol>
				<BodyCol>
					<P>{modernExam.p1}</P>
					<P>{modernExam.p2}</P>
					<P last>{modernExam.p3}</P>
				</BodyCol>
			</Split>
			<ChainBox>
				<Chain>
					{modernExam.chain.map((step, index) => (
						<React.Fragment key={step.label}>
							{index > 0 && (
								<>
									<ArrowH>→</ArrowH>
									<ArrowV>↓</ArrowV>
								</>
							)}
							<ChainPill variant={step.variant}>{step.label}</ChainPill>
						</React.Fragment>
					))}
				</Chain>
				<Closing>{modernExam.closing}</Closing>
			</ChainBox>
		</Container>
	</Section>
)

export default ModernExam

const Section = styled.section`
	background: ${colors.paper};
	padding: 64px 0;
	${MOBILE} {
		padding: 40px 0;
	}
`

const Split = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 32px 72px;
	align-items: flex-start;
	margin-bottom: 40px;
	${MOBILE} {
		display: block;
		margin-bottom: 24px;
	}
`

const HeadCol = styled.div`
	flex: 1 1 380px;
	min-width: 0;
`

const Title = styled.h2`
	font-weight: 700;
	font-size: clamp(24px, 2.2vw, 34px);
	line-height: 1.35;
	color: ${colors.navy};
	margin: 0;
	${MOBILE} {
		font-size: 28px;
		line-height: 1.4;
		margin-bottom: 22px;
	}
`

const BodyCol = styled.div`
	flex: 1 1 380px;
	min-width: 0;
`

const P = styled.p<{ last?: boolean }>`
	margin: 0 0 ${p => (p.last ? '0' : '22px')};
	font-size: 17px;
	line-height: 2.05;
	color: ${colors.body};
	${MOBILE} {
		font-size: 16px;
		line-height: 2;
		margin-bottom: ${p => (p.last ? '0' : '16px')};
	}
`

const ChainBox = styled.div`
	background: ${colors.navy};
	border-radius: 24px;
	padding: 36px 36px;
	text-align: center;
	${MOBILE} {
		border-radius: 20px;
		padding: 28px 22px;
	}
`

const Chain = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
	gap: 14px;
	margin-bottom: 28px;
	${MOBILE} {
		flex-direction: column;
		gap: 9px;
		margin-bottom: 20px;
	}
`

const chainVariants: Record<string, string> = {
	default: `color: #ffffff; background: rgba(245,245,245,.1); border: 1px solid rgba(245,245,245,.2);`,
	red: `color: ${colors.red}; background: rgba(251,54,64,.12); border: 1px solid rgba(251,54,64,.35);`,
	amber: `color: ${colors.navy}; background: ${colors.amber}; border: 1px solid ${colors.amber};`,
}

const ChainPill = styled.span<{ variant: string }>`
	font-weight: 700;
	font-size: clamp(17px, 1.5vw, 22px);
	border-radius: 999px;
	padding: 10px 26px;
	${p => chainVariants[p.variant] ?? chainVariants.default}
	${MOBILE} {
		font-size: 20px;
		padding: 9px 26px;
	}
`

/* The design mirrors the LTR arrow glyph so it points along the RTL reading
   direction on desktop; the mobile artboard stacks the chain with ↓ arrows. */
const ArrowH = styled.span`
	color: ${colors.blue};
	font-size: 22px;
	transform: scaleX(-1);
	display: inline-block;
	${MOBILE} {
		display: none;
	}
`

const ArrowV = styled.span`
	display: none;
	${MOBILE} {
		display: inline-block;
		color: ${colors.blue};
		font-size: 17px;
		line-height: 1;
	}
`

const Closing = styled.p`
	margin: 0;
	font-size: 18px;
	line-height: 1.9;
	color: rgba(245, 245, 245, 0.75);
	${MOBILE} {
		font-size: 15px;
		line-height: 1.85;
	}
`
